"use client";

// Versand planen: Tag und Uhrzeit, dazu drei schnelle Vorschlaege.
import { useState } from "react";
import { CalendarClock } from "lucide-react";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogSubmit,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const LANG = new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

function iso(d: Date) {
  const z = (n: number) => String(n).padStart(2, "0");
  return { tag: `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`, zeit: `${z(d.getHours())}:${z(d.getMinutes())}` };
}

/** Mindestens fuenf Minuten voraus — der Versand-Worker braucht einen Takt. */
function inZukunft(d: Date): boolean {
  return d.getTime() >= Date.now() + 5 * 60_000;
}

function vorschlaege(jetzt: Date): { label: string; d: Date }[] {
  const heute18 = new Date(jetzt);
  heute18.setHours(18, 0, 0, 0);
  const morgen8 = new Date(jetzt);
  morgen8.setDate(morgen8.getDate() + 1);
  morgen8.setHours(8, 0, 0, 0);
  const montag8 = new Date(jetzt);
  montag8.setDate(montag8.getDate() + ((8 - montag8.getDay()) % 7 || 7));
  montag8.setHours(8, 0, 0, 0);
  return [
    ...(heute18 > jetzt ? [{ label: "Heute, 18:00", d: heute18 }] : []),
    { label: "Morgen, 08:00", d: morgen8 },
    { label: "Montag, 08:00", d: montag8 },
  ];
}

export function PlanenDialog({
  offen,
  onClose,
  vorgabe,
  empfaenger,
  onPlanen,
}: {
  offen: boolean;
  onClose: () => void;
  vorgabe: string | null;
  empfaenger: number;
  onPlanen: (zeitpunkt: string) => void;
}) {
  const [jetzt] = useState(() => new Date());
  const [start] = useState(() => (vorgabe ? new Date(vorgabe) : vorschlaege(jetzt)[0]!.d));
  const [tag, setTag] = useState(iso(start).tag);
  const [zeit, setZeit] = useState(iso(start).zeit);
  const [fehler, setFehler] = useState<string | null>(null);
  const gewaehlt = new Date(`${tag}T${zeit || "00:00"}`);
  const gueltig = !Number.isNaN(gewaehlt.getTime());

  function planen() {
    if (!gueltig) return setFehler("Bitte Tag und Uhrzeit angeben.");
    if (!inZukunft(gewaehlt)) return setFehler("Der Zeitpunkt muss mindestens fünf Minuten in der Zukunft liegen.");
    onPlanen(`${tag}T${zeit}:00`);
  }

  return (
    <Dialog open={offen} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>Versand planen</DialogTitle>
        <DialogDescription>
          Die Nachricht geht zum gewählten Zeitpunkt an {empfaenger} Empfänger hinaus. Bis dahin lässt sie sich ändern oder zurücknehmen.
        </DialogDescription>
      </DialogHeader>
      <DialogContent>
        <div className="flex flex-wrap gap-1.5">
          {vorschlaege(jetzt).map((v) => {
            const w = iso(v.d);
            const an = w.tag === tag && w.zeit === zeit;
            return (
              <button
                key={v.label}
                type="button"
                onClick={() => {
                  setTag(w.tag);
                  setZeit(w.zeit);
                  setFehler(null);
                }}
                className={cn(
                  "text-body-sm h-8 rounded-lg px-3 transition-colors",
                  an ? "bg-accent-subtle text-text-primary font-medium" : "bg-bg-secondary text-text-secondary hover:text-text-primary",
                )}
              >
                {v.label}
              </button>
            );
          })}
        </div>
        <div className="mt-4 grid grid-cols-[minmax(0,1fr)_8rem] gap-3">
          <Input id="planen-tag" type="date" label="Tag" value={tag} onChange={(e) => { setTag(e.target.value); setFehler(null); }} />
          <Input id="planen-zeit" type="time" label="Uhrzeit" value={zeit} step={300} onChange={(e) => { setZeit(e.target.value); setFehler(null); }} />
        </div>
        <p className={cn("text-body-sm mt-4 flex items-center gap-2", fehler ? "text-danger" : "text-text-secondary")} role={fehler ? "alert" : undefined}>
          <CalendarClock className="h-4 w-4 shrink-0" aria-hidden />
          {fehler ?? (gueltig ? `Geht am ${LANG.format(gewaehlt)} um ${zeit} Uhr hinaus.` : "Bitte Tag und Uhrzeit angeben.")}
        </p>
      </DialogContent>
      <DialogFooter>
        <DialogCancel />
        <DialogSubmit type="button" onClick={planen}>
          Versand planen
        </DialogSubmit>
      </DialogFooter>
    </Dialog>
  );
}
